import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileText, Image as ImageIcon, Trash2, Sparkles, CheckCircle, Clock, AlertCircle, ArrowRight, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";

export default function UploadTestsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [students, setStudents] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [correcting, setCorrecting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });

  useEffect(() => { loadData(); }, [id]);

  const loadData = async () => {
    try {
      const [sRes, stRes] = await Promise.all([
        api.get(`/sessions/${id}`),
        api.get(`/sessions/${id}/students?show_incomplete=true`),
      ]);
      setSession(sRes.data);
      setStudents(stRes.data);
    } catch {
      toast.error("Erro ao carregar dados");
    }
  };

  const pagesPerTest = session?.pages_per_test || 1;

  const uploadFiles = async (files) => {
    setUploading(true);
    setUploadProgress({ current: 0, total: files.length });
    let lastResult = null;
    for (let i = 0; i < files.length; i++) {
      try {
        const res = await api.upload(`/sessions/${id}/upload-test`, files[i]);
        lastResult = res.data;
        setUploadProgress({ current: i + 1, total: files.length });
      } catch (err) {
        const detail = err.response?.data?.detail || `Erro: ${files[i].name}`;
        toast.error(detail);
      }
    }
    setUploading(false);
    toast.success(`${files.length} ficheiro(s) carregado(s)`);
    await loadData();
  };

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragOver(false);
      const files = Array.from(e.dataTransfer.files).filter(
        (f) => f.type.startsWith("image/") || f.type === "application/pdf"
      );
      if (files.length) uploadFiles(files);
    },
    [id]
  );

  const handleFileInput = (e) => {
    const files = Array.from(e.target.files);
    if (files.length) uploadFiles(files);
    e.target.value = "";
  };

  const deleteStudent = async (studentId) => {
    try {
      await api.delete(`/sessions/${id}/students/${studentId}`);
      setStudents((s) => s.filter((x) => x.id !== studentId));
      toast.success("Teste eliminado");
    } catch {
      toast.error("Erro ao eliminar");
    }
  };

  const correctAll = async () => {
    // Include both "uploaded" and previously failed students for retry
    const pending = students.filter((s) => s.status === "uploaded" || s.status === "error");
    if (!pending.length) {
      toast.info("Todos os testes já foram corrigidos");
      return;
    }
    setCorrecting(true);
    setProgress(0);
    let done = 0;
    let failed = 0;
    for (const student of pending) {
      // Retry up to 2 times per student
      let success = false;
      for (let attempt = 0; attempt < 2 && !success; attempt++) {
        try {
          if (attempt > 0) toast.info(`A tentar novamente #${student.student_number}...`);
          await api.post(`/sessions/${id}/students/${student.id}/correct`, null, { timeout: 180000 });
          done++;
          success = true;
          setProgress(Math.round(((done + failed) / pending.length) * 100));
          setStudents((prev) =>
            prev.map((s) => (s.id === student.id ? { ...s, status: "corrected" } : s))
          );
        } catch {
          if (attempt === 1) {
            failed++;
            toast.error(`Erro no teste #${student.student_number}`);
            setStudents((prev) =>
              prev.map((s) => (s.id === student.id ? { ...s, status: "error" } : s))
            );
            setProgress(Math.round(((done + failed) / pending.length) * 100));
          }
        }
      }
    }
    setCorrecting(false);
    if (failed > 0) {
      toast.warning(`${done} corrigidos, ${failed} falharam. Clique novamente para tentar os que faltam.`);
    } else {
      toast.success("Correção concluída!");
    }
    toast.success("Correção concluída!");
    await loadData();
  };

  const statusIcon = (status) => {
    if (status === "corrected" || status === "reviewed") return <CheckCircle className="h-4 w-4 text-green-600" />;
    if (status === "correcting") return <Clock className="h-4 w-4 text-amber-600 animate-spin" />;
    if (status === "error") return <AlertCircle className="h-4 w-4 text-red-600" />;
    if (status === "incomplete") return <Layers className="h-4 w-4 text-blue-500" />;
    return <Clock className="h-4 w-4 text-muted-foreground" />;
  };

  const completeStudents = students.filter(s => s.status !== "incomplete");
  const incompleteStudents = students.filter(s => s.status === "incomplete");
  const correctedCount = students.filter((s) => ["corrected", "reviewed"].includes(s.status)).length;
  const readyCount = students.filter(s => s.status === "uploaded").length;
  const hasIncomplete = incompleteStudents.length > 0;

  const finalizeCurrentStudent = async () => {
    try {
      await api.post(`/sessions/${id}/finalize-current`);
      toast.success("Aluno finalizado");
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao finalizar");
    }
  };

  const errorCount = students.filter(s => s.status === "error").length;
  const retryCount = readyCount + errorCount;

  return (
    <div className="max-w-3xl mx-auto space-y-8 animate-fade-in" data-testid="upload-tests-page">
      <div>
        <h1 className="font-heading font-bold text-3xl tracking-tight">Carregar Testes</h1>
        <p className="text-muted-foreground mt-1 text-sm">{session?.title} &middot; {session?.subject}</p>
        {pagesPerTest > 1 && (
          <Badge variant="outline" className="mt-2 font-mono">
            <Layers className="h-3 w-3 mr-1" />
            {pagesPerTest} páginas por teste
          </Badge>
        )}
      </div>

      {/* Upload Zone */}
      <div
        className={`border-2 border-dashed rounded-2xl p-12 text-center transition-all duration-200 ${
          dragOver ? "border-primary bg-primary/5 scale-[1.01]" : "border-muted-foreground/20 hover:border-primary/40"
        }`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        data-testid="upload-zone"
      >
        <Upload className="h-12 w-12 mx-auto text-muted-foreground/60 mb-4" />
        <p className="font-medium mb-1">Arraste fotos ou PDFs dos testes</p>
        {pagesPerTest > 1 ? (
          <p className="text-sm text-muted-foreground mb-5">
            Carregue até {pagesPerTest} fotos por aluno.<br/>
            Clique "Finalizar Aluno" quando terminar as páginas de um aluno.
          </p>
        ) : (
          <p className="text-sm text-muted-foreground mb-5">Aceita imagens (JPG, PNG) e ficheiros PDF</p>
        )}
        <input type="file" multiple accept="image/*,.pdf" onChange={handleFileInput} className="hidden" id="file-upload" data-testid="file-input" />
        <label htmlFor="file-upload">
          <Button asChild variant="outline" disabled={uploading}>
            <span>{uploading ? `A carregar ${uploadProgress.current}/${uploadProgress.total}...` : "Selecionar Ficheiros"}</span>
          </Button>
        </label>
        <p className="text-xs text-muted-foreground mt-4 font-mono">{completeStudents.length} teste(s) &middot; máx. 30</p>
      </div>

      {/* Upload Progress */}
      {uploading && (
        <Card className="border-primary/20">
          <CardContent className="py-4">
            <Progress value={(uploadProgress.current / uploadProgress.total) * 100} />
            <p className="text-sm text-muted-foreground mt-2 font-mono">
              A carregar {uploadProgress.current}/{uploadProgress.total} ficheiros...
            </p>
          </CardContent>
        </Card>
      )}

      {/* Incomplete Tests (being built) */}
      {incompleteStudents.length > 0 && (
        <Card className="border-blue-200 bg-blue-50/30">
          <CardHeader>
            <CardTitle className="text-base font-heading flex items-center justify-between">
              <span className="flex items-center gap-2 text-blue-700">
                <Layers className="h-4 w-4" />
                Teste em construção
              </span>
              <Button size="sm" variant="outline" onClick={finalizeCurrentStudent} className="text-blue-700 border-blue-300 hover:bg-blue-100" data-testid="finalize-student-btn">
                <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
                Finalizar Aluno / Próximo Aluno
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1.5">
            {incompleteStudents.map((s) => (
              <div key={s.id} className="flex items-center gap-3 p-2 rounded-lg bg-white/60" data-testid={`incomplete-test-${s.id}`}>
                <span className="font-mono text-sm font-semibold w-16">#{s.student_number}</span>
                <span className="text-xs text-blue-600">
                  {s.page_count || s.pages?.length || 0} de até {pagesPerTest} páginas
                </span>
                <Progress value={((s.page_count || s.pages?.length || 0) / pagesPerTest) * 100} className="flex-1 h-2" />
              </div>
            ))}
            <p className="text-xs text-muted-foreground mt-1">
              Carregue mais páginas ou clique "Finalizar Aluno" para avançar para o próximo.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Complete Tests */}
      {completeStudents.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-heading flex items-center justify-between">
              <span>Testes ({completeStudents.length})</span>
              <Badge variant="outline" className="font-mono">{correctedCount}/{completeStudents.length} corrigidos</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1.5">
            {completeStudents.map((s) => (
              <div key={s.id} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-muted/50 group transition-colors" data-testid={`student-test-${s.id}`}>
                {(s.pages?.[0]?.file_type || s.file_type) === "pdf"
                  ? <FileText className="h-4 w-4 text-red-500 shrink-0" />
                  : <ImageIcon className="h-4 w-4 text-blue-500 shrink-0" />
                }
                <span className="font-mono text-sm font-semibold w-16 shrink-0">#{s.student_number}</span>
                {pagesPerTest > 1 && (
                  <span className="text-xs text-muted-foreground">{s.page_count || s.pages?.length || 1} págs</span>
                )}
                <span className="text-xs text-muted-foreground truncate flex-1">
                  {s.pages?.map(p => p.original_filename).join(", ") || s.original_filename}
                </span>
                {statusIcon(s.status)}
                {s.total_score != null && <Badge variant="outline" className="font-mono text-xs">{s.total_score} pts</Badge>}
                <Button variant="ghost" size="sm" onClick={() => deleteStudent(s.id)} className="opacity-0 group-hover:opacity-100 text-destructive shrink-0" data-testid={`delete-student-${s.id}`}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Correction Progress */}
      {correcting && (
        <Card className="border-primary/20">
          <CardContent className="py-6 space-y-3">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary animate-pulse" />
              <span className="font-medium">A corrigir com IA...</span>
            </div>
            <Progress value={progress} data-testid="correction-progress" />
            <p className="text-sm text-muted-foreground font-mono">{progress}% concluído</p>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={correctAll} disabled={correcting || retryCount === 0} className="active:scale-95 transition-transform" data-testid="correct-all-btn">
          <Sparkles className="h-4 w-4 mr-2" />
          {correcting ? "A corrigir..." : retryCount > 0 ? `Corrigir ${retryCount} teste(s) com IA` : "Todos corrigidos"}
        </Button>
        <Button onClick={() => navigate(`/tests/${id}/correction`)} disabled={correctedCount === 0} className="active:scale-95 transition-transform" data-testid="go-corrections-btn">
          Rever Correções
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
